import React, { useState, useRef, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, Download, Ruler, Pencil } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * enableCalibration / calibration / onSaveCalibration: opt-in scale-ruler support for
 * scaled technical drawings (Design Centre). Left undefined by other callers (Documents,
 * Programme uploads) — this UI only ever renders when a caller explicitly asks for it, so
 * it can never leak into places where "set a real-world scale" would be meaningless.
 *
 * calibration shape (stored on the design doc in Firestore, one entry per PDF page since
 * a single drawing set can mix scales across sheets):
 *   { [pageIndex]: { referenceStart: {x,y}, referenceEnd: {x,y}, realLength, unit, calibratedBy, calibratedAt } }
 * Points are in native PDF point space (72dpi, origin bottom-left) — the same convention
 * every PDF-rendering library (pdf.js here, PDFKit on iOS, PDFium on Android) understands
 * natively, so calibration data set here is directly usable by the mobile measure tools
 * with no translation.
 */
const DrawingViewer = ({ open, onClose, file, enableCalibration, calibration, onSaveCalibration }) => {
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, px: 0, py: 0 });
  const [numPages, setNumPages] = useState(0);
  const viewerBodyRef = useRef(null);

  // Scale ruler: interaction mode + per-page native size (PDF points) captured on load.
  const [interactionMode, setInteractionMode] = useState('view'); // 'view' | 'calibrate' | 'measure'
  const [pageDims, setPageDims] = useState({}); // { [pageIndex]: { width, height } } in PDF points
  const pageContainerRefs = useRef({});
  const [activeDrag, setActiveDrag] = useState(null); // { pageIndex, startPx, currentPx }
  const [calibDraft, setCalibDraft] = useState(null); // frozen line awaiting length input: { pageIndex, startPx, endPx, startPt, endPt }
  const [calibLengthInput, setCalibLengthInput] = useState('');
  const [calibUnit, setCalibUnit] = useState('m');
  const [savingCalibration, setSavingCalibration] = useState(false);
  const [calibrationError, setCalibrationError] = useState('');
  const [measureLine, setMeasureLine] = useState(null); // { pageIndex, startPx, endPx, distanceMeters | notCalibrated }

  const hasAnyCalibration = !!calibration && Object.keys(calibration).length > 0;

  const isImage = file ? (
    /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name || '') ||
    /^image\//i.test(file.fileType || '')
  ) : false;
  const isPDF = file ? (
    /\.pdf$/i.test(file.name || '') ||
    file.fileType === 'application/pdf' ||
    file.fileType === 'pdf'
  ) : false;
  const isDWG = file ? /\.dwg$/i.test(file.name || '') : false;

  useEffect(() => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setIsDragging(false);
    setNumPages(0);
    setInteractionMode('view');
    setPageDims({});
    pageContainerRefs.current = {};
    setActiveDrag(null);
    setCalibDraft(null);
    setCalibLengthInput('');
    setCalibrationError('');
    setMeasureLine(null);
  }, [open, file?.url]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!open) return;
      if (e.key === 'Escape') onClose();
      if (e.key === '=' || e.key === '+') setZoom(z => Math.min(5, +(z + 0.25).toFixed(2)));
      if (e.key === '-') setZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)));
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!isImage || !open) return;
    const el = viewerBodyRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.max(0.25, Math.min(5, +(prev + delta).toFixed(2))));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [isImage, open]);

  const handleMouseDown = (e) => {
    if (!isImage || zoom <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, px: panX, py: panY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPanX(dragStart.px + (e.clientX - dragStart.x));
    setPanY(dragStart.py + (e.clientY - dragStart.y));
  };

  const handleMouseUp = () => setIsDragging(false);

  const resetView = () => { setZoom(1); setPanX(0); setPanY(0); };

  // Hoisted above the scale-ruler helpers (normally computed just before the JSX return)
  // since they close over it: the CSS px width every PDF page renders at, at this zoom.
  const pdfPageWidth = Math.floor(Math.min(window.innerWidth * 0.82, 1000) * zoom);

  // MARK: - Scale ruler: coordinate conversion + drag handling
  // Each page's own native size (pageDims) may differ even though every page renders at
  // the same pdfPageWidth, so the px-per-point scale is computed per page, not assumed
  // uniform across the document. Plain functions (not useCallback) — recreated each render
  // is fine here and it avoids a stale-pdfPageWidth-on-zoom-change dependency-array trap.
  const pixelToPdfPoint = (pageIndex, offsetX, offsetY) => {
    const dims = pageDims[pageIndex];
    if (!dims) return null;
    const scalePxPerPt = pdfPageWidth / dims.width;
    const x = offsetX / scalePxPerPt;
    const y = dims.height - offsetY / scalePxPerPt; // flip to PDF's bottom-left origin
    return { x, y };
  };

  const pdfPointToPixel = (pageIndex, pt) => {
    const dims = pageDims[pageIndex];
    if (!dims) return null;
    const scalePxPerPt = pdfPageWidth / dims.width;
    return { x: pt.x * scalePxPerPt, y: (dims.height - pt.y) * scalePxPerPt };
  };

  const formatDistance = (meters) => (
    meters < 1 ? `${Math.round(meters * 1000)} mm` : `${meters.toFixed(2)} m`
  );

  const UNIT_TO_METERS = { m: 1, cm: 0.01, mm: 0.001 };
  const toMeters = (value, unit) => value * (UNIT_TO_METERS[unit] ?? 1);

  const pointDistance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

  const handlePageMouseDown = (pageIndex, e) => {
    if (interactionMode === 'view') return;
    const container = pageContainerRefs.current[pageIndex];
    if (!container) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = container.getBoundingClientRect();
    const startPx = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setActiveDrag({ pageIndex, startPx, currentPx: startPx });
  };

  useEffect(() => {
    if (!activeDrag) return undefined;
    const handleMove = (e) => {
      const container = pageContainerRefs.current[activeDrag.pageIndex];
      if (!container) return;
      const rect = container.getBoundingClientRect();
      setActiveDrag(prev => prev && ({ ...prev, currentPx: { x: e.clientX - rect.left, y: e.clientY - rect.top } }));
    };
    const handleUp = () => {
      setActiveDrag(prev => {
        if (!prev) return null;
        const { pageIndex, startPx, currentPx } = prev;
        const startPt = pixelToPdfPoint(pageIndex, startPx.x, startPx.y);
        const endPt = pixelToPdfPoint(pageIndex, currentPx.x, currentPx.y);
        if (startPt && endPt && pointDistance(startPx, currentPx) > 4) {
          if (interactionMode === 'calibrate') {
            setCalibDraft({ pageIndex, startPx, endPx: currentPx, startPt, endPt });
            setCalibLengthInput('');
            setCalibrationError('');
          } else if (interactionMode === 'measure') {
            const pageCalib = calibration?.[pageIndex];
            if (!pageCalib) {
              setMeasureLine({ pageIndex, startPx, endPx: currentPx, notCalibrated: true });
            } else {
              const refLengthPts = pointDistance(pageCalib.referenceStart, pageCalib.referenceEnd);
              const realMetersPerPt = toMeters(pageCalib.realLength, pageCalib.unit) / refLengthPts;
              const distanceMeters = pointDistance(startPt, endPt) * realMetersPerPt;
              setMeasureLine({ pageIndex, startPx, endPx: currentPx, distanceMeters });
            }
          }
        }
        return null;
      });
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    // Deliberately depends on activeDrag's pageIndex (a primitive), not the activeDrag
    // object itself — currentPx changes on every mousemove via the functional setState
    // form below, and if the full object were a dependency here, that would tear down
    // and re-attach the window listeners on every single mouse movement mid-drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrag?.pageIndex, interactionMode, calibration]);

  const startCalibrating = () => {
    setInteractionMode('calibrate');
    setMeasureLine(null);
    setCalibDraft(null);
  };

  const startMeasuring = () => {
    setInteractionMode('measure');
    setCalibDraft(null);
    setMeasureLine(null);
  };

  const exitToolMode = () => {
    setInteractionMode('view');
    setActiveDrag(null);
    setCalibDraft(null);
    setMeasureLine(null);
    setCalibrationError('');
  };

  const cancelCalibDraft = () => {
    setCalibDraft(null);
    setCalibLengthInput('');
    setCalibrationError('');
  };

  const saveCalibration = async () => {
    if (!calibDraft) return;
    const realLength = parseFloat(calibLengthInput);
    if (!realLength || realLength <= 0) {
      setCalibrationError('Enter the real-world length of the line you drew.');
      return;
    }
    setSavingCalibration(true);
    setCalibrationError('');
    try {
      await onSaveCalibration({
        pageIndex: calibDraft.pageIndex,
        referenceStart: calibDraft.startPt,
        referenceEnd: calibDraft.endPt,
        realLength,
        unit: calibUnit,
      });
      setCalibDraft(null);
      setCalibLengthInput('');
      setInteractionMode('view');
    } catch (err) {
      setCalibrationError(err?.message || 'Failed to save scale. Please try again.');
    } finally {
      setSavingCalibration(false);
    }
  };

  if (!open || !file) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.93)',
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '14px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
        gap: 16,
        minHeight: 60,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: '#fff', fontWeight: 600, fontSize: 15,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {file.name}
          </div>
          {file.comment && (
            <div style={{
              color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {file.comment}
            </div>
          )}
        </div>

        <a
          href={file.url}
          download={file.name}
          target="_blank"
          rel="noreferrer"
          style={{
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '7px 14px',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          <Download size={14} /> Download
        </a>

        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            width: 36, height: 36,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div
        ref={viewerBodyRef}
        style={{
          flex: 1,
          overflow: isImage ? 'hidden' : 'auto',
          display: 'flex',
          alignItems: isImage ? 'center' : 'flex-start',
          justifyContent: 'center',
          cursor: isImage ? (zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default') : 'default',
          padding: isPDF ? '24px 0' : 0,
          position: 'relative',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {isImage && (
          <img
            src={file.url}
            alt={file.name}
            draggable={false}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
              transformOrigin: '50% 50%',
              transition: isDragging ? 'none' : 'transform 0.08s ease-out',
              userSelect: 'none',
              pointerEvents: 'none',
              display: 'block',
            }}
          />
        )}

        {isPDF && (
          <Document
            file={file.url}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={
              <div style={{ color: 'rgba(255,255,255,0.4)', padding: '60px 40px', textAlign: 'center', fontSize: 14 }}>
                Loading PDF...
              </div>
            }
            error={
              <div style={{ color: '#ef4444', padding: '60px 40px', textAlign: 'center', fontSize: 14 }}>
                Failed to load PDF.
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, i) => {
              const dims = pageDims[i];
              const renderedHeight = dims ? pdfPageWidth * (dims.height / dims.width) : undefined;
              const dragOnThisPage = activeDrag?.pageIndex === i ? activeDrag : null;
              const calibDraftOnThisPage = calibDraft?.pageIndex === i ? calibDraft : null;
              const measureLineOnThisPage = measureLine?.pageIndex === i ? measureLine : null;
              const existingCalib = calibration?.[i];
              const showExistingCalibLine = interactionMode === 'calibrate' && existingCalib && dims;

              return (
                <div
                  key={i}
                  ref={(el) => { pageContainerRefs.current[i] = el; }}
                  onMouseDown={(e) => handlePageMouseDown(i, e)}
                  style={{
                    position: 'relative',
                    marginBottom: 16,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                    borderRadius: 2,
                    cursor: interactionMode !== 'view' ? 'crosshair' : 'default',
                  }}
                >
                  <Page
                    pageNumber={i + 1}
                    width={pdfPageWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    renderMode="canvas"
                    onLoadSuccess={(page) => {
                      setPageDims(prev => ({ ...prev, [i]: { width: page.originalWidth, height: page.originalHeight } }));
                    }}
                  />

                  {dims && (interactionMode !== 'view' || measureLineOnThisPage) && (
                    <svg
                      width={pdfPageWidth}
                      height={renderedHeight}
                      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                    >
                      {showExistingCalibLine && (() => {
                        const a = pdfPointToPixel(i, existingCalib.referenceStart);
                        const b = pdfPointToPixel(i, existingCalib.referenceEnd);
                        return (
                          <g>
                            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#64748b" strokeWidth={2} strokeDasharray="4 4" />
                            <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 8} fill="#94a3b8" fontSize={12} textAnchor="middle">
                              current: {existingCalib.realLength}{existingCalib.unit}
                            </text>
                          </g>
                        );
                      })()}

                      {dragOnThisPage && (
                        <line
                          x1={dragOnThisPage.startPx.x} y1={dragOnThisPage.startPx.y}
                          x2={dragOnThisPage.currentPx.x} y2={dragOnThisPage.currentPx.y}
                          stroke={interactionMode === 'calibrate' ? '#f59e0b' : '#22c55e'}
                          strokeWidth={3}
                        />
                      )}

                      {calibDraftOnThisPage && (
                        <line
                          x1={calibDraftOnThisPage.startPx.x} y1={calibDraftOnThisPage.startPx.y}
                          x2={calibDraftOnThisPage.endPx.x} y2={calibDraftOnThisPage.endPx.y}
                          stroke="#f59e0b" strokeWidth={3}
                        />
                      )}

                      {measureLineOnThisPage && (
                        <g>
                          <line
                            x1={measureLineOnThisPage.startPx.x} y1={measureLineOnThisPage.startPx.y}
                            x2={measureLineOnThisPage.endPx.x} y2={measureLineOnThisPage.endPx.y}
                            stroke="#22c55e" strokeWidth={3}
                          />
                          <text
                            x={(measureLineOnThisPage.startPx.x + measureLineOnThisPage.endPx.x) / 2}
                            y={(measureLineOnThisPage.startPx.y + measureLineOnThisPage.endPx.y) / 2 - 10}
                            fill="#4ade80" fontSize={14} fontWeight={700} textAnchor="middle"
                          >
                            {measureLineOnThisPage.notCalibrated ? 'Page not scaled' : formatDistance(measureLineOnThisPage.distanceMeters)}
                          </text>
                        </g>
                      )}
                    </svg>
                  )}
                </div>
              );
            })}
          </Document>
        )}

        {isDWG && (
          <div style={{ textAlign: 'center', padding: '80px 40px' }}>
            <div style={{ fontSize: 64, fontWeight: 800, color: '#f59e42', letterSpacing: -2, marginBottom: 20 }}>DWG</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, marginBottom: 24 }}>
              CAD files cannot be previewed in the browser.
            </div>
            <a
              href={file.url}
              target="_blank"
              rel="noreferrer"
              style={{
                background: 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                textDecoration: 'none',
                borderRadius: 8, padding: '10px 24px',
                fontWeight: 600, fontSize: 14,
              }}
            >
              Open File
            </a>
          </div>
        )}

        {!isImage && !isPDF && !isDWG && (
          <div style={{ textAlign: 'center', padding: '80px 40px' }}>
            <div style={{ fontSize: 48, color: 'rgba(255,255,255,0.15)', fontWeight: 700, marginBottom: 20 }}>FILE</div>
            <a
              href={file.url}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--branding-app-primary)', textDecoration: 'none', fontSize: 14 }}
            >
              Open in browser →
            </a>
          </div>
        )}

        {/* Scale ruler: instruction banner while calibrating/measuring */}
        {enableCalibration && interactionMode !== 'view' && (
          // Fixed (not sticky) — the body's a flex ROW container (Document/img are
          // siblings), so a sticky/static div here would lay out as another flex item
          // beside the PDF instead of overlaying it.
          <div style={{
            position: 'fixed', top: 76, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(15,23,42,0.92)', color: '#fff',
            padding: '8px 16px', borderRadius: 8, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 12, zIndex: 10,
          }}>
            <span>
              {interactionMode === 'calibrate'
                ? 'Drag a line across a dimension you know the real length of.'
                : 'Drag between two points to measure.'}
            </span>
            <button
              onClick={exitToolMode}
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        )}
      </div>

      {/* Scale ruler: enter the real length of the line just drawn */}
      {calibDraft && (
        <div style={{
          position: 'fixed', left: '50%', bottom: 90, transform: 'translateX(-50%)',
          background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 10, padding: '16px 20px', zIndex: 20,
          display: 'flex', flexDirection: 'column', gap: 10, minWidth: 280,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>What is the real-world length of that line?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              min="0"
              step="any"
              autoFocus
              value={calibLengthInput}
              onChange={(e) => setCalibLengthInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCalibration(); }}
              style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: 14 }}
            />
            <select
              value={calibUnit}
              onChange={(e) => setCalibUnit(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: 14 }}
            >
              <option value="m">metres</option>
              <option value="cm">centimetres</option>
              <option value="mm">millimetres</option>
            </select>
          </div>
          {calibrationError && <div style={{ color: '#f87171', fontSize: 12 }}>{calibrationError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={cancelCalibDraft}
              disabled={savingCalibration}
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={saveCalibration}
              disabled={savingCalibration}
              style={{ background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: savingCalibration ? 'not-allowed' : 'pointer', opacity: savingCalibration ? 0.6 : 1 }}
            >
              {savingCalibration ? 'Saving…' : 'Save scale'}
            </button>
          </div>
        </div>
      )}

      {/* Footer — zoom controls */}
      {(isImage || isPDF) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '12px 20px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          <button
            onClick={() => setZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)))}
            style={{
              background: 'rgba(255,255,255,0.1)', color: '#fff',
              border: 'none', borderRadius: 6, padding: '7px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <ZoomOut size={16} />
          </button>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 500, minWidth: 48, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(z => Math.min(5, +(z + 0.25).toFixed(2)))}
            style={{
              background: 'rgba(255,255,255,0.1)', color: '#fff',
              border: 'none', borderRadius: 6, padding: '7px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <ZoomIn size={16} />
          </button>
          <button
            onClick={resetView}
            style={{
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)',
              border: 'none', borderRadius: 6, padding: '7px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <RotateCcw size={14} />
          </button>
          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, marginLeft: 8 }}>
            {isImage && zoom > 1 ? 'drag to pan · ' : ''}
            {isPDF && numPages > 0 ? `${numPages} page${numPages !== 1 ? 's' : ''} · ` : ''}
            Esc to close
          </span>

          {enableCalibration && isPDF && (
            <>
              <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
              {interactionMode === 'view' ? (
                <>
                  <button
                    onClick={startCalibrating}
                    title={hasAnyCalibration ? 'Recalibrate a page\'s scale' : 'Set this drawing\'s real-world scale'}
                    style={{
                      background: 'rgba(255,255,255,0.1)', color: '#fff',
                      border: 'none', borderRadius: 6, padding: '7px 12px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                    }}
                  >
                    <Pencil size={14} /> {hasAnyCalibration ? 'Recalibrate' : 'Set Scale'}
                  </button>
                  {hasAnyCalibration && (
                    <button
                      onClick={startMeasuring}
                      title="Measure a distance on this drawing"
                      style={{
                        background: 'rgba(255,255,255,0.1)', color: '#fff',
                        border: 'none', borderRadius: 6, padding: '7px 12px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                      }}
                    >
                      <Ruler size={14} /> Measure
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={exitToolMode}
                  style={{
                    background: 'rgba(255,255,255,0.15)', color: '#fff',
                    border: 'none', borderRadius: 6, padding: '7px 12px',
                    cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DrawingViewer;
